import { useState } from "react";

import { toast } from "sonner";

import LazySearchSelect from "../../../components/shared/LazySearchSelect.jsx";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";
import { cn, getCharCount } from "../../../lib/utils.js";

const AttendanceDialog = ({ open, onOpenChange, onSuccess }) => {
  const api = useApi();
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];
  const [newAttendance, setNewAttendance] = useState({
    type: "field",
    projectId: "",
    workerName: "",
    userId: "",
    date: today,
    hoursWorked: "",
    status: "present",
    notes: ""
  });

  const isNotesOverLimit = getCharCount(newAttendance.notes) > 500;

  const createAttendance = async (e) => {
    e.preventDefault();
    if (isNotesOverLimit) {
      toast.error("Notes exceed the 500-character limit.");
      return;
    }

    const requiredFields = [
      { key: "workerName", label: "Worker Name" },
      { key: "date", label: "Date" },
      { key: "hoursWorked", label: "Hours Worked" },
      { key: "status", label: "Status" }
    ];

    if (newAttendance.type === "field") {
      requiredFields.push({ key: "projectId", label: "Project" });
    }

    const missingFields = requiredFields.filter((f) => !newAttendance[f.key]);
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.map((f) => f.label).join(", ")}`);
      return;
    }

    try {
      const payload = {
        ...newAttendance,
        projectId: newAttendance.type === "field" ? newAttendance.projectId : null,
        date: new Date(newAttendance.date).toISOString(),
        hoursWorked: parseFloat(newAttendance.hoursWorked)
      };

      await api.post(`/attendance`, payload);
      if (onSuccess) onSuccess();
      setNewAttendance({
        type: "field",
        projectId: "",
        workerName: "",
        userId: "",
        date: today,
        hoursWorked: "",
        status: "present",
        notes: ""
      });
      onOpenChange(false);
      toast.success("Attendance recorded successfully!");
    } catch (error) {
      toast.error("Failed to record attendance");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>Record Attendance</DialogTitle>
            <DialogDescription>Log worker attendance for the day</DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={createAttendance} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">
                  Attendance Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  onValueChange={(value) =>
                    setNewAttendance({
                      ...newAttendance,
                      type: value,
                      projectId: value === "office" ? "" : newAttendance.projectId
                    })
                  }
                  value={newAttendance.type}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">OFFICE</SelectItem>
                    <SelectItem value="field">FIELD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newAttendance.type === "field" ? (
                <div>
                  <Label htmlFor="projectId">
                    Project <span className="text-red-500">*</span>
                  </Label>
                  <LazySearchSelect
                    value={newAttendance.projectId}
                    onChange={(value) => setNewAttendance({ ...newAttendance, projectId: value })}
                    placeholder="Select project"
                    searchPlaceholder="Search projects..."
                    apiUrl="/projects"
                    pageSize={20}
                    popoverWidth="w-[var(--radix-popover-trigger-width)] md:w-[400px]"
                  />
                </div>
              ) : (
                <div className="hidden md:block" />
              )}

              <div>
                <Label htmlFor="workerName">
                  Worker Name <span className="text-red-500">*</span>
                </Label>
                <LazySearchSelect
                  value={newAttendance.userId}
                  onChange={(value, userObj) => {
                    setNewAttendance({
                      ...newAttendance,
                      workerName: userObj?.name || "",
                      userId: value
                    });
                  }}
                  placeholder="Select worker"
                  searchPlaceholder="Search workers..."
                  apiUrl="/users"
                  dataKey="items"
                  labelKey="name"
                  valueKey="id"
                />
              </div>

              <div>
                <Label htmlFor="date">
                  Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={newAttendance.date}
                  onChange={(e) => setNewAttendance({ ...newAttendance, date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="status">
                  Status <span className="text-red-500">*</span>
                </Label>
                <Select
                  onValueChange={(value) => setNewAttendance({ ...newAttendance, status: value })}
                  value={newAttendance.status}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                    <SelectItem value="half_day">Half Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="hoursWorked">
                  Hours Worked <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="hoursWorked"
                  type="number"
                  step="0.5"
                  value={newAttendance.hoursWorked}
                  onChange={(e) =>
                    setNewAttendance({
                      ...newAttendance,
                      hoursWorked: e.target.value
                    })
                  }
                  placeholder="8.0"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newAttendance.notes}
                  onChange={(e) =>
                    setNewAttendance({
                      ...newAttendance,
                      notes: e.target.value
                    })
                  }
                  className={cn(isNotesOverLimit && "border-red-500")}
                  rows={2}
                  placeholder="Optional notes"
                />
                <WordCount text={newAttendance.notes} />
              </div>
            </div>
          </div>
          <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
            <Button type="submit" variant="primary" className="w-full py-6 rounded-lg shadow-lg" disabled={isNotesOverLimit}>
              Record Attendance
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceDialog;
